<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRecaptchaProvider, useChallengeV3 } from 'vue-recaptcha';
import keyBy from 'lodash-es/keyBy';
import isEmpty from 'lodash-es/isEmpty';

const PROXY_URL = '/sub.php';

const props = defineProps({
    listId: {
        type: String,
        required: true,
    },
    apiKey: {
        type: String,
        required: true,
    },
    successMessage: {
        type: String,
        default: 'Vous vous êtes inscrit avec succès!',
    },
    failureMessage: {
        type: String,
        default: 'Votre inscription a échoué. Veuillez ré-essayer.',
    },
    existingMemberMessage: {
        type: String,
        default: 'Vous êtes déjà inscrit, nous avons mis à jour vos informtations.',
    },
    tags: {
        type: String,
        default: '',
    },
    overrides: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['success', 'error', 'existing']);

const email = ref('');
const formData = ref({});
const formFields = ref([]);
const isSubmitting = ref(false);
const showSuccessMessage = ref(false);
const showFailureMessage = ref(false);
const showExistingMemberMessage = ref(false);
const selectedGroups = ref({});
const formErrors = ref({});

// Initialize reCAPTCHA
useRecaptchaProvider({
    language: 'fr',
});
const { execute } = useChallengeV3('submit');

const overridesRemapped = computed(() => {
    const or = keyBy(
        props.overrides.filter((override) => override.__typename.endsWith('mailchimpFieldOptions_BlockType')),
        'mailchimpFieldMergeTag',
    );
    return or;
});

const groupOverrides = computed(() => props.overrides.filter((override) => override.__typename.endsWith('mailchimpGroup_BlockType')));

/**
 * Applies overrides to a field based on the overridesRemapped object.
 * @param {Object} field - The original field object.
 * @returns {Object|null} - The field object with applied overrides or null if no override exists.
 */
function applyFieldOverrides(field) {
    const override = overridesRemapped.value[field.tag];
    if (!override) return null;

    return {
        ...field,
        name: override.mailchimpFieldLabel || field.name,
        placeholder: override.mailchimpFieldPlaceholder || field.placeholder,
        instructions: override.mailchimpFieldInstructions || field.instructions,
        required: override.mailchimpFieldRequired || field.required,
    };
}

/**
 * Determines if a field type allows placeholders.
 * @param {string} fieldType - The type of the field.
 * @returns {boolean} - Whether the field type allows placeholders.
 */
function fieldAllowsPlaceholder(fieldType) {
    return ['text', 'email', 'number', 'date'].includes(fieldType);
}

/**
 * Filters and organizes form fields based on overrides.
 * @param {Array} fields - The array of form fields to be filtered.
 * @returns {Array} - The filtered and ordered array of form fields.
 */
function filterFields(fields) {
    const emailField = {
        tag: 'EMAIL',
        name: 'Adresse courriel',
        type: 'email',
        required: true,
    };

    const filteredFields = fields.map((field) => applyFieldOverrides(field)).filter((field) => field !== null);

    // Create a map of fields for quick lookup
    const fieldMap = keyBy(filteredFields, 'tag');

    // Order fields based on the overrides prop
    const orderedFields = props.overrides
        .filter((override) => override.__typename.endsWith('mailchimpFieldOptions_BlockType'))
        .map((override) => fieldMap[override.mailchimpFieldMergeTag])
        .filter(Boolean);

    // Ensure EMAIL field is always present and at the beginning
    if (!orderedFields.find((field) => field.tag === 'EMAIL')) {
        orderedFields.unshift(emailField);
    }

    return orderedFields;
}

/**
 * Resets the form data and selected groups.
 */
function resetForm() {
    email.value = '';
    formData.value = {};
    selectedGroups.value = {};
    formErrors.value = {};
}

/**
 * Fetches the list fields from Mailchimp API.
 */
async function fetchListFields() {
    try {
        const params = {
            apiKey: props.apiKey,
            listId: props.listId,
            endpoint: `/lists/${props.listId}/merge-fields`,
        };

        const queryString = new URLSearchParams(Object.fromEntries(Object.entries(params).map(([key, value]) => [key, encodeURIComponent(value)]))).toString();

        const response = await fetch(`${PROXY_URL}?${queryString}`);

        if (!response.ok) throw new Error('Failed to fetch Mailchimp fields');

        const data = await response.json();
        const publicFields = data.merge_fields.filter((field) => field.public !== false);

        formFields.value = filterFields(publicFields);
    } catch (error) {
        console.error('Error fetching Mailchimp fields', error);
        emit('error', error);
    }
}

/**
 * Fetches the groups from Mailchimp API.
 */
async function fetchGroups() {
    try {
        const params = {
            apiKey: props.apiKey,
            listId: props.listId,
            endpoint: `/lists/${props.listId}/interest-categories`,
        };

        const queryString = new URLSearchParams(Object.fromEntries(Object.entries(params).map(([key, value]) => [key, encodeURIComponent(value)]))).toString();

        const response = await fetch(`${PROXY_URL}?${queryString}`);

        if (!response.ok) throw new Error('Failed to fetch Mailchimp groups');

        const data = await response.json();

        const fetchInterests = data.categories
            .filter((category) => groupOverrides.value.some((override) => override.mailchimpGroupId === category.title))
            .map(async (category) => {
                const interestsParams = {
                    apiKey: props.apiKey,
                    listId: props.listId,
                    endpoint: `/lists/${props.listId}/interest-categories/${category.id}/interests`,
                };

                const interestsQueryString = new URLSearchParams(
                    Object.fromEntries(Object.entries(interestsParams).map(([key, value]) => [key, encodeURIComponent(value)])),
                ).toString();

                const interestsResponse = await fetch(`${PROXY_URL}?${interestsQueryString}`);

                if (!interestsResponse.ok) throw new Error('Failed to fetch Mailchimp group interests');

                const interestsData = await interestsResponse.json();

                return {
                    id: category.id,
                    title: category.title,
                    type: category.type,
                    interests: interestsData.interests,
                };
            });

        return await Promise.all(fetchInterests);
    } catch (error) {
        console.error('Error fetching Mailchimp groups', error);
        emit('error', error);
        return [];
    }
}

const processedFormData = computed(() => {
    const processed = { ...formData.value };
    Object.entries(processed).forEach(([key, value]) => {
        if (key.toLowerCase().includes('check_')) {
            processed[key] = value ? 'Oui' : 'Non';
        }
    });
    return processed;
});

/**
 * Determines the input type based on the field properties.
 * @param {Object} field - The field object.
 * @returns {string} - The input type.
 */
function getInputType(field) {
    if (field.type === 'text' && field.tag.toLowerCase().includes('check_')) {
        return 'checkbox';
    }
    const typeMap = {
        text: 'text',
        number: 'number',
        email: 'email',
        radio: 'radio',
        dropdown: 'select',
        date: 'date',
    };
    return typeMap[field.type] || 'text';
}

/**
 * Validates the form fields.
 * @returns {boolean} - Whether the form is valid.
 */
function validateForm() {
    formErrors.value = {};
    let isValid = true;

    formFields.value.forEach((field) => {
        const overriddenField = applyFieldOverrides(field);
        if (overriddenField?.mailchimpFieldRequired === true) {
            if (field.tag === 'EMAIL') {
                if (!email.value) {
                    formErrors.value[field.tag] = 'Ce champ est requis.';
                    isValid = false;
                }
            } else if (!formData.value[field.tag]) {
                formErrors.value[field.tag] = 'Ce champ est requis.';
                isValid = false;
            }
        }
    });

    return isValid;
}

/**
 * Submits the form data to Mailchimp.
 */
async function submitForm() {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;
    showSuccessMessage.value = false;
    showFailureMessage.value = false;
    showExistingMemberMessage.value = false;

    try {
        // Execute reCAPTCHA v3 and get token
        const recaptchaToken = await execute();
        if (recaptchaToken === null) {
            throw new Error("Une erreur de vérification s'est produite");
        }

        const params = {
            apiKey: props.apiKey,
            listId: props.listId,
            endpoint: `/lists/${props.listId}/members/${email.value}`,
            recaptcha_token: recaptchaToken,
        };

        const queryString = new URLSearchParams(Object.fromEntries(Object.entries(params).map(([key, value]) => [key, encodeURIComponent(value)]))).toString();

        const payload = {
            email_address: email.value,
            status: 'subscribed',
            merge_fields: {
                ...processedFormData.value,
                EMAIL: email.value,
            },
        };

        // Check if groups is empty before passing
        if (!isEmpty(selectedGroups.value)) {
            payload.interests = selectedGroups.value;
        }

        // check if tags exist, is a string
        if (props.tags && typeof props.tags === 'string') {
            payload.tags = props.tags.split(',').map((tag) => tag.trim());
        }

        const response = await fetch(`${PROXY_URL}?${queryString}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                payload: JSON.stringify(payload),
                recaptchaToken,
            }),
        });

        const data = await response.json();

        if (response.ok) {
            emit('success');
            showSuccessMessage.value = true;
            resetForm();
        } else if (data.title === 'Member Exists') {
            emit('existing');
            showExistingMemberMessage.value = true;
        } else {
            throw new Error('Mailchimp subscription failed');
        }
    } catch (error) {
        console.error('Mailchimp subscription error', error);
        emit('error', error);
        showFailureMessage.value = true;
    } finally {
        isSubmitting.value = false;
    }
}

onMounted(async () => {
    await fetchListFields();
    const fetchedGroups = await fetchGroups();

    // Create a map of fetched groups for quick lookup
    const groupMap = keyBy(fetchedGroups, 'title');

    // Order fields and groups based on the overrides prop
    const orderedFieldsAndGroups = props.overrides
        .map((override) => {
            if (override.__typename.endsWith('mailchimpFieldOptions_BlockType')) {
                return formFields.value.find((field) => field.tag === override.mailchimpFieldMergeTag);
            }
            if (override.__typename.endsWith('mailchimpGroup_BlockType')) {
                const group = groupMap[override.mailchimpGroupId];
                if (group) {
                    return {
                        tag: `GROUP_${group.id}`,
                        name: group.title,
                        type: 'group',
                        interests: group.interests,
                    };
                }
            }
            return null;
        })
        .filter(Boolean);

    formFields.value = orderedFieldsAndGroups;
});
</script>

<template>
    <form
        class="space-y-4"
        v-on:submit.prevent="submitForm"
    >
        <div
            v-for="field in formFields"
            :key="field.tag"
            class="flex flex-col"
        >
            <template v-if="field.type !== 'group'">
                <label
                    v-if="getInputType(field) !== 'checkbox'"
                    :for="field.tag"
                    class="mb-1 text-sm font-medium"
                >
                    {{ applyFieldOverrides(field).name }}
                    <span
                        v-if="applyFieldOverrides(field).required"
                        class="text-dark-red"
                        >*</span
                    >
                </label>
                <template v-if="field.tag === 'EMAIL'">
                    <input
                        id="email"
                        v-model="email"
                        type="email"
                        :required="applyFieldOverrides(field).required"
                        :placeholder="applyFieldOverrides(field).placeholder"
                        class="border-gray-300 focus:ring-blue-500 rounded-md border px-3 py-2 focus:outline-none focus:ring-2"
                        :class="{ 'border-dark-red': formErrors[field.tag] }"
                    />
                </template>
                <template v-else-if="getInputType(field) === 'select'">
                    <select
                        :id="field.tag"
                        v-model="formData[field.tag]"
                        :name="field.tag"
                        :required="applyFieldOverrides(field).required"
                        class="border-gray-300 focus:ring-blue-500 rounded-md border px-3 py-2 focus:outline-none focus:ring-2"
                        :class="{ 'border-dark-red': formErrors[field.tag] }"
                    >
                        <option
                            value=""
                            disabled
                            selected
                        >
                            {{ applyFieldOverrides(field).placeholder || 'Sélectionner une option' }}
                        </option>
                        <option
                            v-for="choice in field.options.choices"
                            :key="choice"
                            :value="choice"
                        >
                            {{ choice }}
                        </option>
                    </select>
                </template>
                <template v-else-if="getInputType(field) === 'radio'">
                    <div
                        v-for="choice in field.options.choices"
                        :key="choice"
                        class="flex items-center"
                    >
                        <input
                            :id="`${field.tag}-${choice}`"
                            v-model="formData[field.tag]"
                            type="radio"
                            :name="field.tag"
                            :value="choice"
                            :required="applyFieldOverrides(field).required"
                            class="mr-2"
                            :class="{ 'border-dark-red': formErrors[field.tag] }"
                        />
                        <label :for="`${field.tag}-${choice}`">{{ choice }}</label>
                    </div>
                </template>
                <template v-else-if="getInputType(field) === 'checkbox'">
                    <div class="flex items-center">
                        <input
                            :id="field.tag"
                            v-model="formData[field.tag]"
                            type="checkbox"
                            :name="field.tag"
                            :required="applyFieldOverrides(field).required"
                            class="mr-2"
                            :class="{ 'border-dark-red': formErrors[field.tag] }"
                        />
                        <label :for="field.tag">
                            {{ applyFieldOverrides(field).name }}
                            <span
                                v-if="applyFieldOverrides(field).required"
                                class="text-dark-red"
                                >*</span
                            >
                        </label>
                    </div>
                </template>
                <template v-else>
                    <input
                        :id="field.tag"
                        v-model="formData[field.tag]"
                        :type="getInputType(field)"
                        :name="field.tag"
                        :required="applyFieldOverrides(field).required"
                        :placeholder="fieldAllowsPlaceholder(getInputType(field)) ? applyFieldOverrides(field).placeholder : undefined"
                        class="border-gray-300 focus:ring-blue-500 rounded-md border px-3 py-2 focus:outline-none focus:ring-2"
                        :class="{ 'border-dark-red': formErrors[field.tag] }"
                    />
                </template>
                <p
                    v-if="applyFieldOverrides(field).instructions"
                    class="mt-1 text-sm opacity-75"
                >
                    {{ applyFieldOverrides(field).instructions }}
                </p>
                <p
                    v-if="formErrors[field.tag]"
                    class="mt-1 text-sm text-dark-red"
                >
                    {{ formErrors[field.tag] }}
                </p>
            </template>
            <template v-else>
                <h3 class="mb-2 font-medium">
                    {{ field.name }}
                </h3>
                <div
                    v-for="interest in field.interests"
                    :key="interest.id"
                    class="flex items-center"
                >
                    <input
                        :id="`interest-${interest.id}`"
                        v-model="selectedGroups[interest.id]"
                        type="checkbox"
                        :name="`group[${field.tag}][${interest.id}]`"
                        class="mr-2"
                    />
                    <label :for="`interest-${interest.id}`">{{ interest.name }}</label>
                </div>
            </template>
        </div>
        <button
            type="submit"
            :disabled="isSubmitting"
            class="Btn is-normal group inline-flex w-full items-center rounded-xl bg-dark-indigo px-4 py-2 text-base font-medium text-white md:w-auto"
            :class="{
                'bg-light-gray text-dark-gray': isSubmitting,
            }"
        >
            <span class="Btn__text mr-2">{{ isSubmitting ? 'Envoi...' : 'Soumettre' }}</span>
            <Icon
                class="ml-auto w-4 transition duration-500 group-hover:translate-x-2"
                type="arrow"
                svgclass="fill-current"
            />
        </button>
    </form>
    <div
        v-if="showSuccessMessage"
        class="mt-4 rounded bg-dark-green p-2 text-white"
    >
        {{ successMessage }}
    </div>
    <div
        v-if="showFailureMessage"
        class="mt-4 rounded bg-dark-red p-2 text-white"
    >
        {{ failureMessage }}
    </div>
    <div
        v-if="showExistingMemberMessage"
        class="mt-4 rounded bg-dark-yellow p-2 text-white"
    >
        {{ existingMemberMessage }}
    </div>
</template>
